import React, { Component, ReactNode, ErrorInfo } from 'react';
import { captureError } from '../lib/datahub/errorLogging/captureError';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Global Error Boundary
 *
 * Catches unhandled React errors and logs them to DataHub error tracking.
 * Displays a user-friendly fallback UI when errors occur.
 *
 * Uses unified DataHub error logging for:
 * - Consistent error tracking across frontend and backend
 * - Error grouping and deduplication
 * - Google Chat notifications for critical errors
 */
class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Handle chunk load errors (stale cache after deployment) - auto-reload
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Unexpected token');

    if (isChunkError) {
      const lastReload = sessionStorage.getItem('chunk_error_reload');
      const now = Date.now();

      if (!lastReload || (now - parseInt(lastReload, 10)) > 60000) {
        sessionStorage.setItem('chunk_error_reload', now.toString());
        window.location.reload();
        return;
      }
    }

    // Log error to DataHub error tracking
    captureError({
      error,
      capturePoint: 'error_boundary',
      severity: 'critical',
      category: 'react_render',
      componentName: 'ErrorBoundary',
      additionalContext: {
        componentStack: errorInfo.componentStack,
      },
    });

    // Also log to console for development
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Show custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
          <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-10 w-10 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  Something went wrong
                </h1>
                <p className="text-slate-600 mt-1">
                  An unexpected error occurred. The error has been logged and our team will investigate.
                </p>
              </div>
            </div>

            {error && process.env.NODE_ENV === 'development' && (
              <div className="mt-6">
                <h2 className="text-sm font-semibold text-slate-900 mb-2">
                  Error Details (Development Only)
                </h2>
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-sm text-red-800 font-mono whitespace-pre-wrap break-words">
                    {error.toString()}
                  </p>
                </div>
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 bg-navy-600 text-white rounded-md hover:bg-navy-700 transition-colors font-medium"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50 transition-colors font-medium"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

/**
 * Export ErrorBoundary component
 * No longer needs a wrapper since we use captureError directly
 */
export function ErrorBoundary({ children, fallback }: Props) {
  return (
    <ErrorBoundaryClass fallback={fallback}>
      {children}
    </ErrorBoundaryClass>
  );
}
